import { lireCommentaireAction } from "./planningCrmUtils";

const dateIso = (valeur) => {
  const texte = String(valeur || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(texte) ? texte : "";
};
const normaliser = (valeur) => String(valeur || "").trim().toLocaleLowerCase("fr-FR");
const codeClient = (document) => String(document?.donnees?.code_client || "").trim().toUpperCase();
const numeroCommande = (document) => String(document?.donnees?.numero_commande || (document?.type_document === "COMMANDE" ? document?.identifiant_source : "") || "").trim().toUpperCase();
const numeroFacture = (document) => String(document?.donnees?.numero_facture || document?.donnees?.numero_piece || (["FACTURE", "AVOIR"].includes(document?.type_document) ? document?.identifiant_source : "") || "").trim().toUpperCase();
const dateDocument = (document) => dateIso(document?.donnees?.date_paiement || document?.donnees?.date_reglement || document?.donnees?.date_imputation || document?.donnees?.date_facturation || document?.donnees?.date_facture || document?.donnees?.date_creation);
const montantDocument = (document) => Math.abs(Number(document?.donnees?.montant_impute ?? document?.donnees?.montant_ttc ?? document?.donnees?.montant ?? 0));
const arrondir = (valeur) => Math.round(Number(valeur || 0) * 100) / 100;

function actionLiee(actions, ligne, reference, commercial) {
  const ref = normaliser(reference);
  const clubId = String(ligne.club_id || ligne.clubId || "");
  const clubCode = normaliser(ligne.clubCode || ligne.code_client || ligne.codeClient);
  const commercialCible = normaliser(ligne.commercial || ligne.commercial_commande || commercial);
  const correspondances = (actions || []).flatMap((action) => {
    const lu = lireCommentaireAction(action.commentaire);
    const meta = lu.meta || {};
    const referenceAction = normaliser(meta.reference || meta.element_lie);
    const memeDocument = Boolean(ref && referenceAction === ref);
    const memeClub = Boolean((clubId && String(action.club_id || "") === clubId)
      || (clubCode && normaliser(action.clubCode) === clubCode));
    const commercialAction = normaliser(action.commercial || meta.responsable);
    if (!memeDocument || !memeClub || (commercialCible && commercialAction && commercialAction !== commercialCible)) return [];
    const statut = normaliser(meta.statut || "planifie");
    const faite = ["faite", "termine", "terminee", "terminée"].includes(statut);
    const annulee = ["annule", "annulee", "annulé", "annulée"].includes(statut);
    return [{ ...action, meta, faite, annulee, statutAction: annulee ? "ACTION ANNULÉE" : faite ? "ACTION FAITE" : "ACTION EXISTANTE", dateAction: dateIso(meta.annule_le || meta.termine_le || action.date_rdv) }];
  }).sort((a, b) => String(b.dateAction || b.updated_at || "").localeCompare(String(a.dateAction || a.updated_at || "")));
  return correspondances[0] || null;
}

function enrichirAction(ligne, actions, reference, commercial, dossierOuvert) {
  const action = actionLiee(actions, ligne, reference, commercial);
  return {
    ...ligne,
    actionExistante: action,
    actionFaite: Boolean(action?.faite),
    actionAnnulee: Boolean(action?.annulee),
    statutAction: action?.statutAction || "AUCUNE ACTION",
    dateAction: action?.dateAction || "",
    actionFaiteDossierOuvert: Boolean(action?.faite && dossierOuvert)
  };
}

export function etatActuelDetteHistorique(lignes = [], documents = [], options = {}) {
  const dateMaj = dateIso(options.dateDerniereMaj);
  return lignes.map((source) => {
    const d2 = dateIso(source.date_arrete || options.dateArrete);
    const code = String(source.code_client || source.codeClient || "").trim().toUpperCase();
    const facture = String(source.numero_facture || "").trim().toUpperCase();
    const commande = String(source.numero_commande || "").trim().toUpperCase();
    const correspondants = (documents || []).filter((document) => codeClient(document) === code && (
      numeroFacture(document) === facture || (commande && numeroCommande(document) === commande)
    ));
    const mouvements = correspondants.filter((document) => ["PAIEMENT", "AVOIR"].includes(document.type_document));
    const nonDates = mouvements.filter((document) => !dateDocument(document));
    const posterieurs = mouvements.filter((document) => {
      const date = dateDocument(document);
      return date && (!d2 || date > d2) && (!dateMaj || date <= dateMaj);
    }).sort((a, b) => dateDocument(a).localeCompare(dateDocument(b)));
    const historique = arrondir(source.reste_a_payer);
    let cumul = 0;
    let dateResolution = "";
    posterieurs.forEach((document) => {
      cumul = arrondir(cumul + montantDocument(document));
      if (!dateResolution && cumul + 0.009 >= historique) dateResolution = dateDocument(document);
    });
    const certifie = Boolean(dateMaj) && nonDates.length === 0;
    const reste = certifie ? arrondir(Math.max(0, historique - cumul)) : null;
    const statut = !certifie ? "NON_CERTIFIE" : reste <= 0.009 ? "RESOLU" : reste + 0.009 < historique ? "PARTIEL" : "A_TRAITER";
    const ligne = {
      ...source,
      dateEtatActuel: dateMaj,
      etatActuelCertifie: certifie,
      statutActuel: statut,
      resteDuActuel: reste,
      dateResolution,
      resolu: statut === "RESOLU"
    };
    return enrichirAction(ligne, options.actions, facture, options.commercial, statut !== "RESOLU");
  });
}

export function etatActuelAFacturerHistorique(lignes = [], documents = [], options = {}) {
  const dateMaj = dateIso(options.dateDerniereMaj);
  return lignes.map((source) => {
    const d2 = dateIso(options.dateArrete || source.dateArrete);
    const code = String(source.codeClient || source.code_client || "").trim().toUpperCase();
    const commande = String(source.numeroCommande || source.numero_commande || "").trim().toUpperCase();
    const correspondants = (documents || []).filter((document) => codeClient(document) === code && numeroCommande(document) === commande);
    const factures = correspondants.filter((document) => document.type_document === "FACTURE");
    const nonDatees = factures.filter((document) => !dateDocument(document));
    const posterieures = factures.filter((document) => {
      const date = dateDocument(document);
      return date && (!d2 || date > d2) && (!dateMaj || date <= dateMaj);
    }).sort((a, b) => dateDocument(a).localeCompare(dateDocument(b)));
    const montantHistorique = arrondir(source.montant);
    let factureDepuis = 0;
    let dateResolution = "";
    posterieures.forEach((document) => {
      factureDepuis = arrondir(factureDepuis + montantDocument(document));
      if (!dateResolution && factureDepuis + 0.009 >= montantHistorique) dateResolution = dateDocument(document);
    });
    const commandeCourante = correspondants.filter((document) => document.type_document === "COMMANDE")
      .sort((a, b) => String(b.synchronise_at || b.updated_at || "").localeCompare(String(a.synchronise_at || a.updated_at || "")))[0];
    const statutCommande = normaliser(commandeCourante?.donnees?.statut || commandeCourante?.donnees?.etat || commandeCourante?.statut);
    const statutFactureSansPreuve = statutCommande.includes("factur") && posterieures.length === 0;
    const certifie = Boolean(dateMaj) && nonDatees.length === 0 && !statutFactureSansPreuve;
    const reste = certifie ? arrondir(Math.max(0, montantHistorique - factureDepuis)) : null;
    const statut = !certifie ? "NON_CERTIFIE" : reste <= 0.009 ? "RESOLU" : reste + 0.009 < montantHistorique ? "PARTIEL" : "A_TRAITER";
    const ligne = {
      ...source,
      dateEtatActuel: dateMaj,
      etatActuelCertifie: certifie,
      statutActuel: statut,
      montantActuelAFacturer: reste,
      dateResolution,
      dateFacturation: dateResolution || (posterieures[0] ? dateDocument(posterieures[0]) : ""),
      resolu: statut === "RESOLU"
    };
    return enrichirAction(ligne, options.actions, commande, options.commercial, statut !== "RESOLU");
  });
}

const cleDette = (ligne) => `${String(ligne.code_client || "").toUpperCase()}|${String(ligne.numero_facture || "").toUpperCase()}`;
const cleCommande = (ligne) => `${String(ligne.codeClient || "").toUpperCase()}|${String(ligne.numeroCommande || "").toUpperCase()}`;

export function enrichirDetteHistorique(dette, documents = [], options = {}) {
  if (!dette) return null;
  const toutes = [dette.nonEchue, ...(dette.tranches || []).filter((x) => x.label !== "TOTAL"), dette.dateIndisponible]
    .filter(Boolean).flatMap((tranche) => tranche.lignes || []);
  const enrichies = etatActuelDetteHistorique(toutes, documents, { ...options, dateArrete: dette.dateArrete });
  const parCle = new Map(enrichies.map((ligne) => [cleDette(ligne), ligne]));
  const decorer = (tranche) => tranche ? { ...tranche, lignes: (tranche.lignes || []).map((ligne) => parCle.get(cleDette(ligne)) || ligne) } : tranche;
  return {
    ...dette,
    nonEchue: decorer(dette.nonEchue),
    tranches: (dette.tranches || []).map((tranche) => tranche.label === "TOTAL" ? tranche : decorer(tranche)),
    dateIndisponible: decorer(dette.dateIndisponible),
    lignes: enrichies,
    dateEtatActuel: dateIso(options.dateDerniereMaj),
    etatActuelCertifie: Boolean(dateIso(options.dateDerniereMaj)) && enrichies.every((ligne) => ligne.etatActuelCertifie)
  };
}

export function enrichirAFacturerHistorique(stock, documents = [], options = {}) {
  if (!stock) return null;
  const enrichies = etatActuelAFacturerHistorique(stock.lignes || stock.total?.lignes || [], documents, { ...options, dateArrete: stock.reference || options.dateArrete });
  const parCle = new Map(enrichies.map((ligne) => [cleCommande(ligne), ligne]));
  const decorer = (tranche) => tranche ? { ...tranche, lignes: (tranche.lignes || []).map((ligne) => parCle.get(cleCommande(ligne)) || ligne) } : tranche;
  return {
    ...stock,
    tranches: (stock.tranches || []).map(decorer),
    dateIndisponible: decorer(stock.dateIndisponible),
    total: stock.total ? { ...stock.total, lignes: enrichies } : stock.total,
    lignes: enrichies,
    dateEtatActuel: dateIso(options.dateDerniereMaj),
    etatActuelCertifie: Boolean(dateIso(options.dateDerniereMaj)) && enrichies.every((ligne) => ligne.etatActuelCertifie)
  };
}

export function resumeEtatActuel(lignes = [], montantCle = "resteDuActuel") {
  return lignes.reduce((resume, ligne) => {
    if (ligne.statutActuel === "RESOLU") resume.resolus += 1;
    else if (ligne.statutActuel === "NON_CERTIFIE") resume.nonCertifies += 1;
    else {
      resume.aTraiter += 1;
      resume.montant = arrondir(resume.montant + Number(ligne[montantCle] || 0));
    }
    return resume;
  }, { aTraiter: 0, montant: 0, resolus: 0, nonCertifies: 0 });
}
